import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import {
  Q7_2_PACK_OPTIONS,
  readQ7_2Answers,
  writeQ7_2Answers,
} from '@/lib/q7_2Storage';
import { isQ7_1Complete } from '@/lib/q7_1Storage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectPdpaConsent, selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const checkboxLabelClass =
  'flex cursor-pointer items-start gap-3 text-left text-[15px] font-medium leading-snug text-white/95 sm:text-[16px]';

const checkboxClass =
  'mt-0.5 h-[18px] w-[18px] shrink-0 rounded border border-slate-300/90 bg-white text-amber-800 focus:ring-1 focus:ring-slate-300';

export default function Q7_2() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const [selected, setSelected] = useState<string[]>(() => readQ7_2Answers()?.selected ?? []);
  const navigateAfterCheckRef = useRef(false);

  useEffect(() => {
    if (!navigateAfterCheckRef.current) {
      return;
    }
    navigateAfterCheckRef.current = false;
    navigate('/7_3');
  }, [selected, navigate]);

  const toggleOption = (label: string) => {
    setSelected((prev) => {
      const wasChecked = prev.includes(label);
      const next = wasChecked ? [] : [label];
      writeQ7_2Answers({ selected: next });
      if (!wasChecked && next.length > 0) {
        navigateAfterCheckRef.current = true;
      }
      return next;
    });
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  if (!isQ7_1Complete()) {
    return <Navigate to="/7_1" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
        <div className="mx-auto flex w-full max-w-full flex-col py-6">
          <SurveyBrownPanelInset>
            <SurveyBrownPanel className="!p-4">
              <p className="m-0 text-left text-[16px] font-medium leading-snug text-white/95 sm:text-[17px]">
                {t.q7_2.question}
              </p>
              <div className="mt-5 flex flex-col gap-3">
                {Q7_2_PACK_OPTIONS.map((opt) => {
                  const id = `q72-${opt.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')}`;
                  const checked = selected.includes(opt);
                  return (
                    <label key={opt} htmlFor={id} className={checkboxLabelClass}>
                      <input
                        id={id}
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOption(opt)}
                        className={checkboxClass}
                      />
                      <span>{opt}</span>
                    </label>
                  );
                })}
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
